// Team Chat REST layer, against the chat service (`backend/Chat`, `/api/chat/*`). Every write,
// the chosen status and typing signals included, goes through here (persistence + authz +
// audit); the real-time connection only pushes. `/chat/*` endpoints resolve to the chat
// service's base for the active region (resolveChatBase in ./client), so apiFetch needs no base
// of its own. See src/lib/team/teamHub.ts for the socket side.
import { apiFetch, apiRequest } from '@/lib/api/client';
export async function getTeamContext() {
    return apiFetch('/chat/context');
}
/** The chosen status, relayed to every roster the user is on. The service accepts one change a
 * second and answers 429 to the rest: that refusal reaches the caller (teamHub.setStatus retries
 * the newest choice after the window), because a dropped change would leave the optimistic
 * status showing something the service never saved. */
export async function setTeamStatus(status) {
    await apiRequest('POST', '/chat/status', { status });
}
/** A typing signal; the service relays at most one per user and conversation every 2 s and
 * drops the rest (still 204), so callers need no throttle of their own. */
export async function sendTeamTyping(conversationId) {
    await apiRequest('POST', `/chat/conversations/${conversationId}/typing`);
}
export async function getTeamMessages(conversationId, options = {}) {
    const params = new URLSearchParams();
    if (options.before)
        params.set('before', options.before);
    if (options.limit)
        params.set('limit', String(options.limit));
    const qs = params.toString();
    return apiFetch(`/chat/conversations/${conversationId}/messages${qs ? `?${qs}` : ''}`);
}
/** A text message, or a thread reply with replyToId. Frames and collab invites
 * are posted by their own endpoints. */
export async function sendTeamMessage(conversationId, body, options = {}) {
    return apiRequest('POST', `/chat/conversations/${conversationId}/messages`, {
        body,
        replyToId: options.replyToId,
    });
}
export async function markTeamConversationRead(conversationId) {
    await apiRequest('POST', `/chat/conversations/${conversationId}/read`);
}
/** Author only: anyone else's message answers 404. A root takes its replies with it. */
export async function deleteTeamMessage(messageId) {
    await apiRequest('DELETE', `/chat/messages/${messageId}`);
}
export async function reactToTeamMessage(messageId, emoji, remove = false) {
    await apiRequest('POST', `/chat/messages/${messageId}/reactions`, { emoji, remove });
}
export async function createTeamChannel(groupId, name) {
    return apiRequest('POST', '/chat/conversations', { groupId, name });
}
/** Group-admin only: soft-archive a channel (hidden everywhere, history kept). */
export async function archiveTeamChannel(conversationId) {
    await apiRequest('POST', `/chat/conversations/${conversationId}/archive`);
}
/** One thread (root + replies, oldest first). Accepts a reply id too — the
 * server resolves to the root. */
export async function getTeamThread(messageId) {
    return apiFetch(`/chat/messages/${messageId}/replies`);
}
export async function createTeamDm(userId) {
    return apiRequest('POST', '/chat/dms', { userId });
}
export async function getOrCreateCaseThread(caseId) {
    return apiRequest('POST', `/chat/cases/${caseId}/thread`);
}
/** Shares a viewport capture. `seriesId` is the series' database id (SeriesSummary.id), not
 * its DICOM UID: the service checks it against the audience's series scope. */
export async function shareTeamFrame(conversationId, dataUrl, options) {
    return apiRequest('POST', `/chat/conversations/${conversationId}/frames`, {
        dataUrl,
        caption: options.caption,
        studyId: options.studyId,
        seriesId: options.seriesId,
        caseId: options.caseId,
    });
}
/** A shared frame's record with a signed CDN URL valid until `expiresAt` (ten minutes); the
 * bytes never pass through the chat service. 404 when the frame was retracted or this reader
 * may no longer see it. */
export async function getTeamFrame(frameId) {
    return apiFetch(`/chat/frames/${frameId}`);
}
/** Starts a live session; the response carries the presenter's room grant. */
export async function startCollabSession(params) {
    return apiRequest('POST', '/chat/collab', params);
}
/** The session, with a FRESH single-use room grant on every call (null for a read-only
 * member or an ended session): call it to join, again before the grant's `expiresAt`, and
 * after the room expires. */
export async function getCollabSession(sessionId) {
    return apiFetch(`/chat/collab/${sessionId}`);
}
export async function endCollabSession(sessionId) {
    await apiRequest('POST', `/chat/collab/${sessionId}/end`);
}
